import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  Package,
  Users,
  FileText,
  Calendar,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Plus,
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, trend, color = 'primary' }) => (
  <Card className="card-hover" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl md:text-3xl font-heading font-bold">{value}</p>
          {trend && (
            <p className="text-xs text-green-500 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {trend}
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { token, user } = useAuthStore();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">
            Bonjour, {user?.full_name?.split(' ')[0] || 'Utilisateur'}
          </h1>
          <p className="text-muted-foreground">
            Voici un aperçu de votre activité
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="new-quote-btn">
            <Link to="/quotes">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau devis
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Produits"
          value={stats?.counts?.products || 0}
          icon={Package}
          color="primary"
        />
        <StatCard
          title="Clients"
          value={stats?.counts?.clients || 0}
          icon={Users}
          color="primary"
        />
        <StatCard
          title="Devis"
          value={stats?.counts?.quotes || 0}
          icon={FileText}
          color="primary"
        />
        <StatCard
          title="Événements"
          value={stats?.counts?.events || 0}
          icon={Calendar}
          color="primary"
        />
      </div>

      {/* Revenue Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Chiffre d'affaires (devis acceptés)</p>
              <p className="text-3xl md:text-4xl font-heading font-bold">
                {formatCurrency(stats?.total_revenue || 0)}
              </p>
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-500">{stats?.quotes_by_status?.draft || 0}</p>
                <p className="text-xs text-muted-foreground">Brouillons</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-500">{stats?.quotes_by_status?.sent || 0}</p>
                <p className="text-xs text-muted-foreground">Envoyés</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-500">{stats?.quotes_by_status?.accepted || 0}</p>
                <p className="text-xs text-muted-foreground">Acceptés</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent & Upcoming */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Quotes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading">Derniers devis</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/quotes">
                Voir tout <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.recent_quotes?.length > 0 ? (
              stats.recent_quotes.map((quote) => (
                <Link
                  key={quote.id}
                  to={`/quotes/${quote.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  data-testid={`recent-quote-${quote.id}`}
                >
                  <div>
                    <p className="font-medium">{quote.reference}</p>
                    <p className="text-sm text-muted-foreground">{quote.client_name || 'Sans client'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(quote.total_ttc)}</p>
                    <Badge variant="outline" className={getStatusColor(quote.status)}>
                      {quote.status}
                    </Badge>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-4">Aucun devis récent</p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading">Événements à venir</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/events">
                Voir tout <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.upcoming_events?.length > 0 ? (
              stats.upcoming_events.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-secondary/50"
                  data-testid={`upcoming-event-${event.id}`}
                >
                  <div>
                    <p className="font-medium">{event.title}</p>
                    <p className="text-sm text-muted-foreground">{event.client_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatDate(event.start_date)}</p>
                    <p className="text-xs text-muted-foreground">{event.location}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-4">Aucun événement à venir</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {stats?.low_stock_products?.length > 0 && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2 text-yellow-600 dark:text-yellow-500">
              <AlertTriangle className="w-5 h-5" />
              Stock faible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.low_stock_products.map((product) => (
                <Badge
                  key={product.id}
                  variant="outline"
                  className="bg-yellow-500/10 text-yellow-600 dark:text-yellow-500 border-yellow-500/30"
                >
                  {product.name}: {product.stock_quantity} restant(s)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
